import React, { useState, useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus, faUserGroup, faHourglassHalf, faCheckCircle, faUser, faClock, faCheck, faTimes } from "@fortawesome/free-solid-svg-icons";
import { toast } from "react-toastify";
import { primaryColor } from "../../../../../utils/comon";
import MetricCard from "../../../../common/Card/MetricCard";
import PageHeadingSection from "../../../../common/pageheading/PageHeadingSection";
import CustomSelect from "../../../../common/formfields/CustomSelect";
import apiServices from "../../../../../services/apiService";
import Breadcrumbs from "../../../../common/BreadCrum/BreadCrum";
import ExtendVisitDurationModal from "./ExtendVisitDurationModal";
import DeleteModal from "../../../../common/commonmodal/DeleteModal";
import ListSkeleton from "../../../../common/Skeleton/ListSkeleton";



const VisitorRequest = () => {
    const navigate = useNavigate();
    const { user } = useSelector((state) => state.auth);
    const siteId = user?.siteId;
    const unitId = user?.branchorunitID;
    const roleName = (user?.roleName).toLowerCase();
    const isSiteAdmin = roleName.includes('site admin') || roleName.includes('super');

    const [searchQuery, setSearchQuery] = useState("");
    const [statusFilter, setStatusFilter] = useState("Status");
    const [dateFilter, setDateFilter] = useState("");
    const [loading, setLoading] = useState(false);
    const [invitedVisitors, setInvitedVisitors] = useState([]);
    const [openMenuId, setOpenMenuId] = useState(null);
    const [isExtendModalOpen, setIsExtendModalOpen] = useState(false);
    const [extendingVisitor, setExtendingVisitor] = useState(null);
    const [isBlockConfirmOpen, setIsBlockConfirmOpen] = useState(false);
    const [blockingVisitor, setBlockingVisitor] = useState(null);

    const formatEntryTime = (isoString) => {
        if (!isoString) return "—";
        const d = new Date(isoString);
        if (isNaN(d.getTime())) return "—";
        const dateStr = d.toLocaleDateString("en-US", { month: "2-digit", day: "2-digit", year: "2-digit" });
        const timeStr = d.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", hour12: true });
        return `${dateStr} ${timeStr}`;
    };


    const handleOpenExtendModal = (row) => {
        setExtendingVisitor(row);
        setIsExtendModalOpen(true);
    };

    const [pagination, setPagination] = useState({
        pageNumber: 1,
        pageSize: 10,
        totalRecords: 0
    });

    const statusOptions = [
        { label: "All Status", value: "Status" },
        { label: "Pending", value: "PENDING" },
        { label: "Approved", value: "APPROVED" },
        { label: "Pass Issued", value: "PASS_ISSUED" },
        { label: "Checked-In", value: "CHECKED_IN" },
        { label: "Checked-Out", value: "CHECKED_OUT" },
        { label: "Rejected", value: "REJECTED" }
    ];

    const fetchInvitedVisitors = async () => {
        try {
            setLoading(true);
            let url = `/Visitor/getAllVisitor?pageNumber=${pagination.pageNumber}&pageSize=${pagination.pageSize}&visitorTypeId=1`;
            if (siteId) url += `&siteId=${siteId}`;
            if (!isSiteAdmin && unitId) {
                url += `&unitId=${unitId}`;
            }
            if (searchQuery) {
                url += `&searchText=${encodeURIComponent(searchQuery)}`;
            }

            const response = await apiServices({
                method: "GET",
                url: url,
            });
            if (response?.success) {
                const rawData = response?.data?.data || response?.data;
                const apiData = Array.isArray(rawData) ? rawData : [];
                setInvitedVisitors(apiData);

                const total = response?.data?.totalCount || response?.data?.totalRecords || response?.data?.total || response?.totalRecords || apiData.length;
                setPagination(prev => ({ ...prev, totalRecords: total }));
            }
        } catch (error) {
            console.error("Error fetching invited visitors:", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchInvitedVisitors();
    }, [pagination.pageNumber, pagination.pageSize, searchQuery]);

    const handleCheckOut = async (row) => {
        try {
            setLoading(true);
            const payload = {
                statusId: 4, // Checkout status
                unitId: row?.unitid || row?.unitId
            };
            const response = await apiServices({
                method: "PUT",
                url: `/Visitor/updateVisitorStatus/${row?.mobile || row?.visitorMobileNumber}`,
                data: payload,
            });
            if (response?.success || response?.isSuccess) {
                toast.success("Visitor checked out successfully");
                fetchInvitedVisitors();
            } else {
                toast.error(response?.message || "Failed to checkout visitor");
            }
        } catch (error) {
            console.error(error);
            toast.error("Failed to checkout visitor");
        } finally {
            setLoading(false);
        }
    };

    const handleBlockVisitor = async (row) => {
        try {
            setLoading(true);
            const payload = {
                statusId: 6, // Blocked status
                unitId: row?.unitid || row?.unitId
            };
            const response = await apiServices({
                method: "PUT",
                url: `/Visitor/updateVisitorStatus/${row?.mobile || row?.visitorMobileNumber}`,
                data: payload,
            });
            if (response?.success || response?.isSuccess) {
                toast.success("Visitor blocked successfully");
                fetchInvitedVisitors();
            } else {
                toast.error(response?.message || "Failed to block visitor");
            }
        } catch (error) {
            console.error(error);
            toast.error("Failed to block visitor");
        } finally {
            setLoading(false);
        }
    };



    const filteredInvitedVisitors = invitedVisitors.filter((visitor) => {
        const query = searchQuery.toLowerCase();

        const matchesSearch = !searchQuery ||
            (visitor?.visitorName || "").toLowerCase().includes(query) ||
            (visitor?.visitorMobileNumber || "").toLowerCase().includes(query) ||
            (visitor?.PurposeOfVisit || "").toLowerCase().includes(query) ||
            (visitor?.siteName || "").toLowerCase().includes(query);

        const normalizedStatus = (visitor?.status || "").toUpperCase().replaceAll("_", "-").replaceAll(" ", "-");
        const filterStatusNorm = statusFilter.replaceAll("_", "-").toUpperCase();
        const matchesStatus = statusFilter === "Status" || normalizedStatus === filterStatusNorm;

        const matchesDate = !dateFilter || (visitor?.expectedEntryTime && new Date(visitor?.expectedEntryTime).toISOString().split("T")[0] === dateFilter);

        return matchesSearch && matchesStatus && matchesDate;
    });

    const totalRequests = invitedVisitors.length;
    const pendingRequests = invitedVisitors.filter(v => (v?.status || "").toUpperCase() === "PENDING").length;
    const approvedToday = invitedVisitors.filter(v => {
        const s = (v?.status || "").toUpperCase();
        return s === "PASS_ISSUED" || s === "APPROVED" || s === "PASS ISSUED";
    }).length;

    const getStatusStyles = (status) => {
        const normalizedStatus = (status || "").toUpperCase().replace(/[-_ ]/g, "");
        switch (normalizedStatus) {
            case "CHECKEDIN": return "bg-[#004B591A] text-[#004B59]";
            case "CHECKEDOUT":
            case "CHECKOUT": return "bg-[#E31F262B] text-[#E31F26]";
            case "PENDING": return "bg-[#FEF9C3] text-[#D97706]";
            case "APPROVED": return "bg-[#DCFCE7] text-[#166534]";
            case "REJECTED": return "bg-[#E31F26] text-[#FFFFFF]";
            case "BLOCKED":
            case "BLOCK": return "bg-[#0F172A] text-white border border-[#0F172A]";
            default: return "bg-gray-100 text-gray-700";
        }
    };

    const tableActions = (
        <div className="flex flex-col sm:flex-row items-center gap-3 w-full sm:w-auto">
            <div className="relative flex-1 w-full sm:w-64">
                <span className="absolute inset-y-0 left-0 flex items-center pl-3.5 pointer-events-none text-gray-400">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"></path></svg>
                </span>
                <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => {
                        setSearchQuery(e.target.value);
                        setPagination(prev => ({ ...prev, pageNumber: 1 }));
                    }}
                    placeholder="Search visitors..."
                    className="w-full pl-10 pr-4 py-2 border border-gray-200 dark:border-bordercolor rounded-lg text-sm focus:outline-none bg-transparent text-textprimary hover:border-gray-400"
                />
            </div>
            <div className="w-full sm:w-40 relative">
                <CustomSelect
                    options={statusOptions}
                    value={statusOptions.find(opt => opt.value === statusFilter)}
                    onChange={(opt) => setStatusFilter(opt.value)}
                    placeholder="Status"
                />
            </div>
            <div className="relative flex items-center bg-[#F8FAFC] dark:bg-actionitembg border border-gray-200 dark:border-bordercolor rounded-lg px-3 py-2 text-sm font-medium text-[#475569] dark:text-textprimary w-full sm:w-auto hover:border-gray-400">
                <input
                    type="date"
                    value={dateFilter}
                    onChange={(e) => setDateFilter(e.target.value)}
                    className="bg-transparent border-none outline-none font-medium text-[#475569] dark:text-textprimary cursor-pointer w-full"
                />
            </div>
        </div>
    );

    const displayedInvitedVisitors = useMemo(() => {
        return filteredInvitedVisitors;
    }, [filteredInvitedVisitors]);

    return (
        <div className="bg-dashboardbg px-6 py-4 border border-bordercolor flex flex-col rounded-lg flex-1 overflow-y-auto gap-4 pt-4 scrollbar-hide">
            <Breadcrumbs />
            <PageHeadingSection
                mainHeading="Invited Visitor List"
                subHeading="Manage all Invited Visitors"
                primaryBtnText="Invite New"
                primaryBtnIcon={faPlus}
                PrimaryOnClick={() => navigate("/zentcomm/visitor-managment/invited-visitor/add")}

            />

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <MetricCard title="TOTAL INVITED" value={String(totalRequests).padStart(2, "0")} icon={faUserGroup} />
                <MetricCard title="PENDING" value={String(pendingRequests).padStart(2, "0")} icon={faHourglassHalf} />
                <MetricCard title="APPROVED" value={String(approvedToday).padStart(2, "0")} icon={faCheckCircle} />
            </div>

            {/* Filter actions panel */}
            <div className="flex flex-col md:flex-row items-center justify-between gap-4 bg-white dark:bg-sectionheaderbg p-4 border border-slate-100 dark:border-bordercolor rounded-2xl shadow-sm">
                <h3 className="font-bold text-sm text-[#0F172A] dark:text-textprimary">Visitors</h3>
                {tableActions}
            </div>

            <div className="shrink-0 flex-1 flex flex-col gap-4">
                {/* Custom Card List */}
                <div className="space-y-4">
                    {loading ? (
                        <ListSkeleton count={4} />
                    ) : filteredInvitedVisitors.length === 0 ? (
                        <div className="flex flex-col items-center justify-center py-16 px-4 border border-dashed border-slate-200 dark:border-bordercolor rounded-2xl bg-white dark:bg-sectionheaderbg text-center gap-3">
                            <div className="w-12 h-12 rounded-full bg-slate-50 dark:bg-actionitembg border border-slate-200 dark:border-bordercolor flex items-center justify-center text-slate-400 dark:text-textsecondary">
                                <FontAwesomeIcon icon={faUser} className="text-lg" />
                            </div>
                            <div className="flex flex-col gap-1">
                                <h5 className="font-bold text-slate-700 dark:text-textprimary text-sm">No Visitors Found</h5>
                                <p className="text-xs text-slate-400 dark:text-textsecondary max-w-[280px]">We couldn't find any invited visitors matching your search or filters.</p>
                            </div>
                        </div>
                    ) : displayedInvitedVisitors.map((row, index) => {
                        const currentStatus = (row?.status || "").toUpperCase().replace(/[-_ ]/g, "");
                        const isCheckedIn = currentStatus === "CHECKEDIN" || currentStatus === "PASSISSUED" || currentStatus === "APPROVED";

                        return (
                            <div key={row.visitorId || index} className="bg-white dark:bg-sectionheaderbg border border-gray-100 dark:border-bordercolor rounded-2xl shadow-sm hover:shadow-md transition-shadow flex flex-col">
                                {/* Main Card Content */}
                                <div className="py-3 sm:px-4 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                                    {/* Left: Avatar & Info */}
                                    <div className="flex items-center gap-4">
                                        {row.image ? (
                                            <img src={row.image} className="w-10 h-10 rounded-full border border-gray-100 dark:border-bordercolor object-cover" alt={row.visitorName} />
                                        ) : (
                                            <div className="w-10 h-10 rounded-full bg-slate-50 dark:bg-actionitembg border border-slate-200 dark:border-bordercolor flex items-center justify-center text-slate-400 dark:text-textsecondary">
                                                <FontAwesomeIcon icon={faUser} className="text-sm" />
                                            </div>
                                        )}
                                        <div className="flex flex-col gap-0.5">
                                            <h4 className="font-bold text-[13px] text-[#1E293B] dark:text-textprimary">{row?.visitorName || "—"}</h4>
                                            <span className="text-[11px] font-semibold text-slate-500 dark:text-textsecondary">{row?.visitorMobileNumber ? `+91 ${row.visitorMobileNumber}` : "—"}</span>
                                            <span className="text-[10px] text-slate-400 dark:text-textsecondary font-medium flex items-center gap-1 mt-0.5">
                                                <svg className="w-3.5 h-3.5 text-slate-400 dark:text-textsecondary" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1" />
                                                </svg>
                                                Entry: {formatEntryTime(row?.expectedEntryTime || row?.createdDate)}
                                            </span>
                                        </div>
                                    </div>

                                    {/* Right: Actions */}
                                    <div className="flex flex-col items-end gap-3 self-end sm:self-center">

                                        <div className="flex items-center gap-3">
                                            {/* Status Badge */}
                                            <span className={`px-3 py-1 rounded-full text-[9px] font-black tracking-wider uppercase ${getStatusStyles(row?.status)}`}>
                                                {(row?.status || "PENDING").replaceAll("_", "-")}
                                            </span>



                                            {isCheckedIn && (
                                                <div className="flex items-center gap-2 relative">
                                                    <button
                                                        onClick={() => handleCheckOut(row)}
                                                        className="bg-[#E31F26] text-white font-black text-[10px] px-3.5 py-2 rounded-xl flex items-center gap-2 shadow-sm hover:bg-red-700 transition-all active:scale-95"
                                                    >
                                                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1" />
                                                        </svg>
                                                        Check-Out
                                                    </button>

                                                    {/* Action Dropdown Menu */}
                                                    <div className="relative">
                                                        <button
                                                            onClick={() => setOpenMenuId(openMenuId === row.visitorId ? null : row.visitorId)}
                                                            className="w-8 h-8 rounded-full bg-slate-100 dark:bg-actionitembg text-slate-600 dark:text-textsecondary flex items-center justify-center hover:bg-slate-200 dark:hover:bg-cardbg transition-all"
                                                        >
                                                            <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 24 24">
                                                                <path d="M12 8c1.1 0 2-.9 2-2s-.9-2-2-2-2.9.9-2 2 .9 2 2 2zm0 2c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm0 6c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z" />
                                                            </svg>
                                                        </button>
                                                        {openMenuId === row.visitorId && (
                                                            <div className="absolute right-0 mt-2 w-44 bg-white dark:bg-cardbg border border-gray-100 dark:border-bordercolor rounded-xl shadow-xl z-50 py-1 animate-in fade-in slide-in-from-top-1 duration-150">
                                                                <button
                                                                    onClick={() => {
                                                                        setOpenMenuId(null);
                                                                        handleOpenExtendModal(row);
                                                                    }}
                                                                    className="w-full flex items-center gap-2 px-3 py-2 text-slate-700 dark:text-textprimary hover:bg-slate-50 dark:hover:bg-actionitembg transition-colors text-[11px] font-bold text-left"
                                                                >
                                                                    <svg className="w-3.5 h-3.5 text-slate-500 dark:text-textsecondary" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                                                    </svg>
                                                                    Extend Visit Duration
                                                                </button>
                                                                <button
                                                                    onClick={() => {
                                                                        setOpenMenuId(null);
                                                                        setBlockingVisitor(row);
                                                                        setIsBlockConfirmOpen(true);
                                                                    }}
                                                                    className="w-full flex items-center gap-2 px-3 py-2 text-red-600 hover:bg-red-50/50 dark:hover:bg-red-950/20 transition-colors text-[11px] font-bold text-left"
                                                                >
                                                                    <svg className="w-3.5 h-3.5 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
                                                                    </svg>
                                                                    Blacklist Visitor
                                                                </button>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                {/* Bottom Details Grid */}
                                <div className="bg-[#F8FAFC] dark:bg-actionitembg border-t border-slate-100 dark:border-bordercolor px-4 py-2 sm:px-3 rounded-b-2xl">
                                    <div className="grid grid-cols-2 sm:grid-cols-5 gap-4 text-xs">
                                        <div className="flex flex-col gap-0.5">
                                            <span className="text-[9px] font-bold text-slate-400 dark:text-textsecondary uppercase tracking-wider">Location</span>
                                            <span className="text-slate-700 dark:text-textprimary font-semibold text-[11px] truncate max-w-[150px]" title={row?.location}>{row?.location || "—"}</span>
                                        </div>
                                        <div className="flex flex-col gap-0.5">
                                            <span className="text-[9px] font-bold text-slate-400 dark:text-textsecondary uppercase tracking-wider">Joiners</span>
                                            <span className="text-slate-700 dark:text-textprimary font-semibold text-[11px]">{row?.additionalCount || 0}</span>
                                        </div>
                                        <div className="flex flex-col gap-0.5">
                                            <span className="text-[9px] font-bold text-slate-400 dark:text-textsecondary uppercase tracking-wider">Purpose</span>
                                            <span className="text-slate-700 dark:text-textprimary font-semibold text-[11px] truncate max-w-[150px]" >{row?.purposeOfVisit || "—"}</span>
                                        </div>
                                        <div className="flex flex-col gap-0.5">
                                            <span className="text-[9px] font-bold text-slate-400 dark:text-textsecondary uppercase tracking-wider">Unit</span>
                                            <span className="text-slate-700 dark:text-textprimary font-semibold text-[11px]">{row?.unitName || "—"}</span>
                                        </div>
                                        <div className="flex flex-col gap-0.5">
                                            <span className="text-[9px] font-bold text-slate-400 dark:text-textsecondary uppercase tracking-wider">VisitorType</span>
                                            <span className="text-slate-700 dark:text-textprimary font-semibold text-[11px] truncate max-w-[150px]">{row?.visitorType || "—"}</span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        );
                    })
                    }
                </div>
                {/* Custom Pagination Footer */}
                {pagination.totalRecords > 0 && (
                    <div className="flex flex-col md:flex-row items-center justify-between gap-4 mt-6 pt-4 border-t border-gray-100 dark:border-bordercolor">
                        <div className="flex flex-wrap items-center gap-4 text-xs font-bold text-[#64748B] dark:text-textsecondary">
                            <span>
                                Showing <span className="text-[#0F172A] dark:text-textprimary">{Math.min((pagination.pageNumber - 1) * pagination.pageSize + 1, pagination.totalRecords)}</span> to <span className="text-[#0F172A] dark:text-textprimary">{Math.min(pagination.pageNumber * pagination.pageSize, pagination.totalRecords)}</span> of <span className="text-[#0F172A] dark:text-textprimary">{pagination.totalRecords}</span> entries
                            </span>
                            <div className="flex items-center gap-1.5">
                                <span>Show</span>
                                <select
                                    value={pagination.pageSize}
                                    onChange={(e) => {
                                        const newSize = Number(e.target.value);
                                        setPagination(prev => ({ ...prev, pageSize: newSize, pageNumber: 1 }));
                                    }}
                                    className="border border-gray-200 dark:border-bordercolor rounded-lg px-2 py-1 bg-white dark:bg-actionitembg font-bold text-[#475569] dark:text-textprimary outline-none text-xs"
                                >
                                    {[10, 25, 50, 100].map((size) => (
                                        <option key={size} value={size}>{size}</option>
                                    ))}
                                </select>
                                <span>entries</span>
                            </div>
                        </div>

                        <div className="flex items-center gap-2">
                            <button
                                disabled={pagination.pageNumber === 1}
                                onClick={() => setPagination(prev => ({ ...prev, pageNumber: Math.max(1, prev.pageNumber - 1) }))}
                                className={`flex items-center justify-center px-4 h-8 rounded-lg border text-xs font-bold transition-all duration-200
                                  ${pagination.pageNumber === 1
                                        ? "bg-gray-50 dark:bg-actionitembg text-gray-300 dark:text-gray-600 border-gray-100 dark:border-bordercolor cursor-not-allowed"
                                        : "bg-white dark:bg-cardbg text-[#64748B] dark:text-textsecondary border-gray-200 dark:border-bordercolor hover:border-red-500 hover:text-red-500"
                                    }`}
                            >
                                Back
                            </button>
                            <div className="flex items-center gap-1">
                                {Array.from({ length: Math.ceil(pagination.totalRecords / pagination.pageSize) || 1 }, (_, i) => i + 1).map((page) => {
                                    const isCurrent = pagination.pageNumber === page;
                                    return (
                                        <button
                                            key={page}
                                            onClick={() => setPagination(prev => ({ ...prev, pageNumber: page }))}
                                            className={`w-8 h-8 rounded-lg text-xs font-bold transition-all duration-200
                                            ${isCurrent
                                                    ? "text-white"
                                                    : "bg-white dark:bg-cardbg text-[#64748B] dark:text-textsecondary border border-gray-200 dark:border-bordercolor hover:border-red-500 hover:text-red-500"
                                                }`}
                                            style={isCurrent ? { backgroundColor: primaryColor(), borderColor: primaryColor() } : {}}
                                        >
                                            {page}
                                        </button>
                                    );
                                })}
                            </div>
                            <button
                                disabled={pagination.pageNumber === (Math.ceil(pagination.totalRecords / pagination.pageSize) || 1)}
                                onClick={() => setPagination(prev => ({ ...prev, pageNumber: Math.min(Math.ceil(prev.totalRecords / prev.pageSize) || 1, prev.pageNumber + 1) }))}
                                className={`flex items-center justify-center px-4 h-8 rounded-lg border text-xs font-bold transition-all duration-200
                                  ${pagination.pageNumber === (Math.ceil(pagination.totalRecords / pagination.pageSize) || 1)
                                        ? "bg-gray-50 dark:bg-actionitembg text-gray-300 dark:text-gray-600 border-gray-100 dark:border-bordercolor cursor-not-allowed"
                                        : "bg-white dark:bg-cardbg text-[#64748B] dark:text-textsecondary border-gray-200 dark:border-bordercolor hover:border-red-500 hover:text-red-500"
                                    }`}
                            >
                                Next
                            </button>
                        </div>
                    </div>
                )}
            </div>


            {/* Extend Visit Duration Modal */}
            <ExtendVisitDurationModal
                isOpen={isExtendModalOpen}
                onClose={() => setIsExtendModalOpen(false)}
                visitor={extendingVisitor}
            />

            {/* Blacklist Confirmation DeleteModal */}
            <DeleteModal
                isOpen={isBlockConfirmOpen}
                onClose={() => {
                    setIsBlockConfirmOpen(false);
                    setBlockingVisitor(null);
                }}
                onConfirm={async () => {
                    if (blockingVisitor) {
                        await handleBlockVisitor(blockingVisitor);
                    }
                    setIsBlockConfirmOpen(false);
                    setBlockingVisitor(null);
                }}
                title="Blacklist Visitor"
                description={`Are you sure you want to blacklist ${blockingVisitor?.visitorName || "this visitor"}? This action will block their future entry.`}
                confirmBtnText="Blacklist"
                cancelBtnText="Cancel"
                isLoading={loading}
            />
        </div>
    );
};

export default VisitorRequest;